// src/components/Sidebar.js
import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaHome,
  FaBox,
  FaShoppingCart,
  FaUsers,
  FaCog,
  FaSignOutAlt,
  FaComments,
  FaStar,
  FaStore,
  FaSearch
} from 'react-icons/fa';
import AuthContext from '../context/AuthContext';

const Sidebar = () => {
  const { logout, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const adminItems = [
    { path: '/dashboard', icon: FaHome, label: 'Dashboard' },
    { path: '/products', icon: FaBox, label: 'Products' },
    { path: '/orders', icon: FaShoppingCart, label: 'Orders' },
    { path: '/customers', icon: FaUsers, label: 'Customers' },
    { path: '/queries', icon: FaComments, label: 'Queries' },
    { path: '/reviews', icon: FaStar, label: 'Reviews' },
    { path: '/seo', icon: FaSearch, label: 'SEO' },
    { path: '/settings', icon: FaCog, label: 'Settings' },
  ];

  // SEO staff only ever see the SEO workspace.
  const seoItems = [{ path: '/seo', icon: FaSearch, label: 'SEO' }];
  const menuItems = user && user.role === 'seo' ? seoItems : adminItems;

  const isActive = (path) => location.pathname === path;

  return (
    <motion.aside
      initial={{ x: -280 }}
      animate={{ x: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="fixed left-0 top-0 h-screen w-64 bg-sidebar-bg flex flex-col z-40"
    >
      {/* Logo Section */}
      <div className="p-6 border-b border-sidebar-border">
        <Link to="/dashboard" className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
            <FaStore className="text-white text-lg" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg tracking-tight">Admin Panel</h1>
            <p className="text-slate-400 text-xs">Crochet Store</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6 px-3 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              className={`
                flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200
                ${active
                  ? 'bg-primary-600 text-white shadow-lg shadow-primary-600/30'
                  : 'text-slate-400 hover:bg-sidebar-hover hover:text-white'
                }
              `}
            >
              <Icon className={`text-lg ${active ? 'text-white' : ''}`} />
              <span className="font-medium">{item.label}</span>
              {active && (
                <motion.div
                  layoutId="activeIndicator"
                  className="ml-auto w-1.5 h-1.5 bg-white rounded-full"
                />
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Section & Logout */}
      <div className="p-4 border-t border-sidebar-border">
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200"
        >
          <FaSignOutAlt className="text-lg" />
          <span className="font-medium">Logout</span>
        </button>
      </div>
    </motion.aside>
  );
};

export default Sidebar;
